/**
 * Shopify Storefront MCP Client
 * Handles interactions with the Storefront MCP server via the /api/mcp endpoint.
 */
class StorefrontMCP {
    constructor() {
        this.storeDomain = window.location.hostname;
        this.mcpEndpoint = `https://${this.storeDomain}/api/mcp`;
    }

    /**
     * Generic method to call an MCP tool.
     * @param {string} name - The name of the tool to call.
     * @param {object} args - The arguments for the tool.
     * @returns {Promise<object>} - The JSON response from the server.
     */
    async callTool(name, args) {
        try {
            const response = await fetch(this.mcpEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jsonrpc: '2.0',
                    method: 'tools/call',
                    id: Date.now(),
                    params: {
                        name: name,
                        arguments: args
                    }
                })
            });

            if (!response.ok) {
                throw new Error(`MCP Server Error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(`MCP Tool Error: ${JSON.stringify(data.error)}`);
            }

            return data.result;
        } catch (error) {
            console.error('StorefrontMCP Error:', error);
            throw error;
        }
    }

    /**
     * Search the shop's catalog.
     * @param {string} query - The search query.
     * @param {string} context - Additional context for the search.
     * @returns {Promise<object>}
     */
    async searchCatalog(query, context = 'customer browsing') {
        return this.callTool('search_shop_catalog', { query, context });
    }

    /**
     * Get shop policies and FAQs.
     * @param {string} query - The question or topic.
     * @param {string} context - Additional context.
     * @returns {Promise<object>}
     */
    async getPolicies(query, context = 'customer inquiry') {
        return this.callTool('search_shop_policies_and_faqs', { query, context });
    }

    /**
     * Get the current cart details.
     * @param {string} cartId - The ID of the cart to retrieve.
     * @returns {Promise<object>}
     */
    async getCart(cartId) {
        return this.callTool('get_cart', { cart_id: cartId });
    }

    /**
     * Update the cart (add/update items).
     * @param {string} cartId - The ID of the cart (optional, creates new if null).
     * @param {Array} lines - Array of objects with {line_item_id, merchandise_id, quantity}.
     * @returns {Promise<object>}
     */
    async updateCart(cartId, lines) {
        return this.callTool('update_cart', { cart_id: cartId, lines });
    }
}

// Expose the instance globally
window.storefrontMCP = new StorefrontMCP();
